import { FormEvent, ReactNode, useEffect, useState } from "react";
import { connect, ConnectedProps, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { ActionsTypes, AuthActions, AuthGState, Mui, MuiIcons } from "@leap4/frontend";
import { RootState } from "../../../interfaces/root-state.interface";
import { BACKEND_SERVICE_NAME, loginUrl, meMultiUrl, requiredAuthRoles, setCookieUrl } from "../../../config/generals";
import { urlService } from "../../routing/urlsService";
import { darkTheme } from "../../../theme/portalTheme";
import { ViewLensWordmark } from "../../layout/ViewLensWordmark";
import { extractErrorMessage } from "../../../utils/functions";

const { ThemeProvider, CssBaseline, Box, Card, CardContent, Typography, TextField, Button, Alert, CircularProgress, IconButton, InputAdornment, Link, useMediaQuery } = Mui;
const { Visibility, VisibilityOff } = MuiIcons;

const mapStateToProps = (state: RootState) => ({ auth: state.auth });
const connector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
interface IProps extends PropsFromRedux {
    auth: AuthGState;
}

type View = "login" | "forgot" | "reset";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_PASSWORD = 8;

const messageOf = (err: unknown, fallback: string): string =>
    extractErrorMessage(err) || (err instanceof Error && err.message) || fallback;

/** The DSP's DarkAuthLayout + SignInPage: always dark, one centred card, no split screen. */
const AuthLayout = ({ children }: { children: ReactNode }) => {
    const compact = useMediaQuery(darkTheme.breakpoints.down("sm"));
    return (
        <ThemeProvider theme={darkTheme}>
            <CssBaseline enableColorScheme />
            <Box component="main" sx={{ minHeight: "100vh", display: "flex", alignItems: compact ? "flex-start" : "center", justifyContent: "center", bgcolor: "background.default", p: compact ? 0 : 2 }}>
                <Card sx={{ maxWidth: 440, width: "100%", ...(compact && { border: "none", borderRadius: 0, minHeight: "100vh", bgcolor: "background.default" }) }}>
                    <CardContent sx={{ p: 4 }}>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, mb: 3 }}>
                            <Box component="img" src="/adte-logo-mark.png" alt="ADTE" sx={{ height: 60, width: "auto" }} />
                            <ViewLensWordmark size="large" />
                        </Box>
                        {children}
                    </CardContent>
                </Card>
            </Box>
        </ThemeProvider>
    );
};

const PasswordField = ({ label, value, onChange, error, autoComplete, autoFocus }: { label: string; value: string; onChange: (v: string) => void; error?: string; autoComplete: string; autoFocus?: boolean }) => {
    const [shown, setShown] = useState(false);
    return (
        <TextField
            label={label}
            type={shown ? "text" : "password"}
            fullWidth
            margin="normal"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            error={!!error}
            helperText={error}
            autoComplete={autoComplete}
            autoFocus={autoFocus}
            slotProps={{
                input: {
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton aria-label={shown ? "Hide password" : "Show password"} onClick={() => setShown(!shown)} edge="end">
                                {shown ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                        </InputAdornment>
                    ),
                },
            }}
        />
    );
};

const LoginContainer = ({ auth }: IProps) => {
    const user = auth.user;
    const history = useHistory();
    const dispatch = useDispatch();

    const resetToken = new URLSearchParams(window.location.search).get("reset-token");
    const [view, setView] = useState<View>(resetToken ? "reset" : "login");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [touched, setTouched] = useState(false);
    const [busy, setBusy] = useState(false);
    const [serverError, setServerError] = useState<string | null>(null);
    const [sent, setSent] = useState(false);
    const [resetDone, setResetDone] = useState(false);

    useEffect(() => {
        if (user) history.replace(urlService.dashboard());
    }, [user, history]);

    if (user) return null;

    const emailError = touched && !EMAIL.test(email.trim()) ? "Enter a valid email address." : undefined;
    const passwordError = touched && !password ? "Enter your password." : undefined;

    const signedIn = (loaded: unknown) => {
        dispatch({ type: ActionsTypes.USER_LOADED, payload: loaded });
        dispatch({ type: ActionsTypes.LOGIN_SUCCESS });
    };

    const goTo = (next: View) => {
        setView(next);
        setServerError(null);
        setTouched(false);
        setSent(false);
        if (next === "login" && resetToken) {
            const url = new URL(window.location.href);
            url.searchParams.delete("reset-token");
            window.history.replaceState({}, "", url.toString());
        }
    };

    const submitLogin = async (e: FormEvent) => {
        e.preventDefault();
        setTouched(true);
        if (!EMAIL.test(email.trim()) || !password) return;
        setBusy(true);
        setServerError(null);
        try {
            const loaded = await AuthActions.login({
                email: email.trim(),
                password,
                meUrl: meMultiUrl,
                setCookieUrl,
                requiredRoles: requiredAuthRoles,
                serviceName: BACKEND_SERVICE_NAME,
                loginUrl,
            });
            signedIn(loaded);
        } catch (err) {
            setServerError(messageOf(err, "That email and password don't match. Try again."));
        } finally {
            setBusy(false);
        }
    };

    const submitForgot = async (e: FormEvent) => {
        e.preventDefault();
        setTouched(true);
        if (!EMAIL.test(email.trim())) return;
        setBusy(true);
        setServerError(null);
        try {
            await AuthActions.forgotPassword(email.trim());
            setSent(true);
        } catch (err) {
            setServerError(messageOf(err, "We couldn't send the reset link. Try again."));
        } finally {
            setBusy(false);
        }
    };

    const submitReset = async (e: FormEvent) => {
        e.preventDefault();
        setTouched(true);
        if (password.length < MIN_PASSWORD || password !== confirm || !resetToken) return;
        setBusy(true);
        setServerError(null);
        try {
            await AuthActions.resetPassword(resetToken, password);
            setResetDone(true);
        } catch (err) {
            setServerError(messageOf(err, "This reset link has expired or was already used. Request a new one."));
        } finally {
            setBusy(false);
        }
    };

    const subtitle = { login: "Sign in to your account", forgot: "Reset your password", reset: "Choose a new password" }[view];
    const submitButton = (label: string, busyLabel: string) => (
        <Button type="submit" variant="contained" fullWidth size="large" disabled={busy} sx={{ mt: 2, mb: 2 }}>
            {busy ? (
                <>
                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                    {busyLabel}
                </>
            ) : (
                label
            )}
        </Button>
    );

    return (
        <AuthLayout>
            <Typography variant="body2" align="center" color="text.secondary" component="h1" sx={{ mb: 3 }}>
                {subtitle}
            </Typography>

            {serverError && (
                <Alert severity="error" role="alert" sx={{ mb: 2 }}>
                    {serverError}
                </Alert>
            )}

            {view === "login" && (
                <Box component="form" onSubmit={submitLogin} noValidate>
                    <TextField label="Email" type="email" fullWidth margin="normal" autoComplete="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} error={!!emailError} helperText={emailError} disabled={busy} />
                    <PasswordField label="Password" value={password} onChange={setPassword} error={passwordError} autoComplete="current-password" />
                    {submitButton("Sign In", "Signing in…")}
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <Link component="button" type="button" variant="body2" onClick={() => goTo("forgot")}>
                            Forgot password?
                        </Link>
                    </Box>
                </Box>
            )}

            {view === "forgot" &&
                (sent ? (
                    <Box role="status">
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            Check your email
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            If an account exists for {email.trim()}, a password reset link is on its way. It points back to this page.
                        </Typography>
                        <Button fullWidth variant="outlined" onClick={() => goTo("login")}>
                            Back to sign in
                        </Button>
                    </Box>
                ) : (
                    <Box component="form" onSubmit={submitForgot} noValidate>
                        <Typography variant="body2" color="text.secondary">
                            Enter your account's email and we'll send you a link to set a new password.
                        </Typography>
                        <TextField label="Email" type="email" fullWidth margin="normal" autoComplete="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} error={!!emailError} helperText={emailError} disabled={busy} />
                        {submitButton("Send reset link", "Sending…")}
                        <Box sx={{ textAlign: "center" }}>
                            <Link component="button" type="button" variant="body2" onClick={() => goTo("login")}>
                                Back to sign in
                            </Link>
                        </Box>
                    </Box>
                ))}

            {view === "reset" &&
                (resetDone ? (
                    <Box role="status">
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            Password updated
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            You can now sign in with your new password.
                        </Typography>
                        <Button fullWidth variant="contained" onClick={() => { setPassword(""); setConfirm(""); goTo("login"); }}>
                            Go to sign in
                        </Button>
                    </Box>
                ) : (
                    <Box component="form" onSubmit={submitReset} noValidate>
                        <PasswordField
                            label="New password"
                            value={password}
                            onChange={setPassword}
                            autoComplete="new-password"
                            autoFocus
                            error={touched && password.length < MIN_PASSWORD ? `Use at least ${MIN_PASSWORD} characters.` : undefined}
                        />
                        <PasswordField
                            label="Confirm new password"
                            value={confirm}
                            onChange={setConfirm}
                            autoComplete="new-password"
                            error={touched && password !== confirm ? "The two passwords don't match." : undefined}
                        />
                        {submitButton("Set new password", "Saving…")}
                        <Box sx={{ textAlign: "center" }}>
                            <Link component="button" type="button" variant="body2" onClick={() => goTo("login")}>
                                Back to sign in
                            </Link>
                        </Box>
                    </Box>
                ))}
        </AuthLayout>
    );
};

export default connector(LoginContainer);
